import { Box, TypographyProps } from '@mui/material';
import { Calendar } from '@vy/ui-design-system';
import { vyDate } from '@vy/utils';
import React from 'react';
import DatePicker from '../../../design/date-picker/DatePicker';
import { EditableLabelRenderProps, EditableLabelWrapper } from './EditableLabelWrapper';

const ISSUE_DATE_FORMAT = 'MMM DD, YYYY';

type EditableDateLabelProps = {
  label: React.ReactNode;
  date: Date;
  onSave: (date: Date) => Promise<void>;
  displayInline?: boolean;
  labelText?: React.ReactNode;
  dateFormat?: (date: Date) => string;
  renderDisplayValue?: (date: Date) => React.ReactNode;
  disabled?: boolean;
  fontWeight?: number | string;
  fontSize?: number | string;
} & Omit<TypographyProps, 'fontWeight' | 'fontSize'>;

const formatDisplayValue = (value: Date): string => vyDate(value).format(ISSUE_DATE_FORMAT);

export const EditableDateLabel: React.FC<EditableDateLabelProps> = ({
  label,
  date,
  onSave,
  displayInline = false,
  labelText,
  renderDisplayValue = formatDisplayValue,
  disabled = false,
  fontWeight,
  fontSize,
  ...typographyProps
}) => {
  const renderDateInput = ({ value, onChange }: EditableLabelRenderProps<Date>) => (
    <DatePicker
      date={value}
      onSelect={(newDate) => {
        if (newDate) {
          onChange(newDate);
        }
      }}
      containerClassName="w-fit"
      buttonContainerClassName="w-fit"
    >
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          backgroundColor: '#CFE6FF',
          padding: '4px 8px',
          borderRadius: '4px',
          fontWeight,
          fontSize,
          cursor: 'pointer',
        }}
      >
        <span>{vyDate(value).format(ISSUE_DATE_FORMAT)}</span>
        <Calendar width={20} height={20} />
      </Box>
    </DatePicker>
  );

  return (
    <EditableLabelWrapper<Date>
      label={label}
      value={date}
      onSave={onSave}
      displayInline={displayInline}
      labelText={labelText}
      renderInput={renderDateInput}
      renderDisplayValue={renderDisplayValue}
      disabled={disabled}
      {...typographyProps}
    />
  );
};
